import {useEffect, useState} from 'react';
import {initializeApp} from 'firebase/app';
import {getPerformance} from 'firebase/performance';
import {getAuth} from 'firebase/auth';
import {
  getFirestore,
  collection,
  doc,
  setDoc,
  onSnapshot,
} from 'firebase/firestore';
import {
  getRemoteConfig,
  isSupported,
  fetchAndActivate,
  getValue,
} from 'firebase/remote-config';
import {BrowserRouter, Switch, Route} from 'react-router-dom';
import {LocalizationProvider} from '@mui/x-date-pickers';
import {AdapterMoment} from '@mui/x-date-pickers/AdapterMoment';
import {Theme, ThemeProvider, StyledEngineProvider} from '@mui/material/styles';
import {useRecoilState, useRecoilValue, useSetRecoilState} from 'recoil';
import LogRocket from 'logrocket';
import * as Sentry from '@sentry/react';
import * as firebaseui from 'firebaseui';
import {
  setDefaultHeaders,
  fetchRequest,
} from '@bidmii/common/lib/util/FetchRequest';
import {HelmetProvider} from 'react-helmet-async';

import {MainRouter} from './modules';
import {isChunkLoadError} from './common/chunkReload';
import {userState, t, tb, bt} from './recoil/user/userAtom';
import {lastMsgState} from './recoil/socket/socketAtom';
import {unreadMessageCountState} from './recoil/messaging/messagingAtom';
import {apiRootState} from './recoil/rootAtom';
import {
  FIREBASE_CONFIG,
  API_URL,
  REQUEST_PROTO,
  REQUEST_PORT,
  CDN_URL,
  FACEBOOK_ID,
  PACKAGE_ID,
  APPLE_ID,
  DEPLOYMENT_ENV,
} from './constants';
import {theme} from './Theme';
import {
  initializeAnalytics,
  setFirebaseUserId,
  fireLogin,
  setFirebaseUserProperties,
} from './util/Analytics';
import {
  removeFooterState,
  removeHeaderState,
} from './recoil/removeAppBar/removeAppBarAtom';
import {initUserFromWebView} from './util/WebViewUtils';
import 'firebaseui/dist/firebaseui.css';
import './App.css';
import {initializeMetaData} from '@bidmii/common/lib/util/MetaDataUtils';
import {BuildEnvironment} from '@bidmii/common/lib/server/IServerApi';
import BetaGate from './common/BetaGate';
import ColdStartOverlay from './common/ColdStartOverlay';

declare module '@mui/styles/defaultTheme' {
  interface DefaultTheme extends Theme {}
}

setDefaultHeaders({
  // @ts-expect-error ts-migrate(2322) FIXME: Type 'string | undefined' is not assignable to typ... Remove this comment to see the full error message
  'x-forwarded-proto': REQUEST_PROTO,
  // @ts-expect-error ts-migrate(2322) FIXME: Type 'string | undefined' is not assignable to typ... Remove this comment to see the full error message
  'x-forwarded-port': REQUEST_PORT,
  'new-fetch-request': 'true',
});

initializeMetaData({
  onServer: false,
  appUrl: `https://${window.location.hostname}`,
  appName: 'Bidmii',
  // @ts-expect-error ts-migrate(2322) FIXME: Type 'string | undefined' is not assignable to typ... Remove this comment to see the full error message
  cdnUrl: CDN_URL,
  facebookAppId: FACEBOOK_ID,
  androidPackage: PACKAGE_ID,
  appleAppStoreId: APPLE_ID,
  nativeAppLaunchUrl: 'https://app.bidmii.com',
  environment: DEPLOYMENT_ENV as BuildEnvironment,
});

export const firebaseApp = initializeApp(FIREBASE_CONFIG);
export const firebaseAuth = getAuth(firebaseApp);
export const firestoreDb = getFirestore(firebaseApp);
const remoteConfig = getRemoteConfig(firebaseApp);

// Firebase Performance, Analytics, and Remote Config fetches each register a
// Firebase "installation". On a contractor Pro Website served from the
// contractor's OWN custom domain, that request is blocked by the web API key's
// HTTP-referrer allowlist (403 PERMISSION_DENIED) and floods Sentry. These are
// main-app telemetry only; the pro-site lead form relies on Auth + Firestore,
// neither of which registers an installation, so those stay enabled everywhere.
// Custom domains only ever point at production, so non-production builds keep
// telemetry on regardless of host.
const onBidmiiOwnedHost =
  DEPLOYMENT_ENV !== 'production' ||
  window.location.hostname === 'localhost' ||
  window.location.hostname === 'bidmii.com' ||
  window.location.hostname.endsWith('.bidmii.com');

if (onBidmiiOwnedHost) {
  getPerformance(firebaseApp);
}

const App = (props: $TSFixMe) => {
  const [systemStartTime] = useState(new Date().getTime());
  const setApiRoot = useSetRecoilState(apiRootState);
  const setLastMsg = useSetRecoilState(lastMsgState);
  const setUnreadMessageCount = useSetRecoilState(unreadMessageCountState);
  const setRemoveHeader = useSetRecoilState(removeHeaderState);
  const setRemoveFooter = useSetRecoilState(removeFooterState);
  const [user, setUser] = useRecoilState(userState);
  // Note, the below code is a stupid hack used to block people from certain time zones reverse engineering Bidmii
  const tvalue = useRecoilValue(t);
  const [btvalue, setBtvalue] = useRecoilState(bt);
  const [tbvalue, setTbvalue] = useRecoilState(tb);
  // End of stupid code locking time zones
  const [enableLogrocket, setEnableLogrocket] = useState(false);
  const [originalLocation] = useState(window.location.pathname);
  const [coldStartVisible, setColdStartVisible] = useState(false);
  const [serverReady, setServerReady] = useState(false);

  useEffect(() => {
    const coldStartTimer = setTimeout(() => setColdStartVisible(true), 2000);
    fetchRequest(`${API_URL}/`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
      priority: 'high',
    })
      .then((res) => res.json())
      .then((res) => {
        clearTimeout(coldStartTimer);
        setServerReady(true);
        setApiRoot(res);
      })
      .catch((err) => {
        clearTimeout(coldStartTimer);
        setServerReady(true);
        Sentry.captureException(err);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /* STORE FIREBASE TOKEN (f) IN SESSIONSTORAGE */
  useEffect(() => {
    const searchParams = new URLSearchParams(window.location.search);
    /* STORE FIREBASE TOKEN (f) IN SESSIONSTORAGE */
    const f = searchParams.get('f');
    if (f) {
      sessionStorage.setItem('Firebase-Authorization', f);
      if (!user?.initialised) {
        console.log('Initializing Firebase Auth For WebView');
        initUserFromWebView(f, (res: $TSFixMe) => setUser(res));
      }
      setRemoveHeader(true);
      setRemoveFooter(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (btvalue.includes(tvalue)) {
      setTbvalue(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [btvalue]);

  /* INIT FIREBASE */
  useEffect(() => {
    if (onBidmiiOwnedHost) {
      initializeAnalytics();
    }
    firebaseAuth.onAuthStateChanged((authUser) => {
      if (authUser) {
        fireLogin(authUser);
        setFirebaseUserId(authUser.uid);
      } else {
        setFirebaseUserId(null);
      }
    });
    isSupported().then((supported) => {
      const isBot =
        ['GoogleBot', 'BingBot', 'AhrefsBot'].includes(navigator.userAgent) ||
        navigator.userAgent.includes('Bot') ||
        navigator.userAgent.includes('rawler');

      if (supported && onBidmiiOwnedHost) {
        remoteConfig.settings.minimumFetchIntervalMillis = 600000;
        fetchAndActivate(remoteConfig)
          .then(() => {
            const log_rocket_enabled = getValue(
              remoteConfig,
              'log_rocket_enabled',
            );
            if (log_rocket_enabled.asBoolean() && !isBot) {
              setEnableLogrocket(true);
            }
            const remotebtValues = getValue(remoteConfig, 'bt');
            if (remotebtValues) {
              setBtvalue(remotebtValues.asString().split(','));
            }
          })
          .catch((err) => {
            // Remote Config timeouts/storage failures are environmental and
            // the in-code defaults take over — not worth a Sentry error.
            process.env.NODE_ENV === 'development' && console.error(err);
          });
      } else {
        const log_rocket_enabled = process.env.REACT_APP_ENABLE_LOG_ROCKET;
        if (log_rocket_enabled === '1' && !isBot) {
          setEnableLogrocket(true);
        }
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /* END INIT FIREBASE */

  /* INIT FIREBASE AUTH */
  if (!firebaseui.auth.AuthUI.getInstance()) {
    // eslint-disable-next-line no-new
    new firebaseui.auth.AuthUI(firebaseAuth);
  }
  /* END INIT FIREBASE AUTH */

  useEffect(() => {
    if (tbvalue) {
      setInterval(function () {
        // @ts-ignore
        if (window.location.pathname !== originalLocation) {
          // @ts-ignore
          window.location.pathname = originalLocation;
        }
      }, 2000);
    }
  }, [tbvalue, originalLocation]);

  const searchParams = new URLSearchParams(window.location.search);

  /* STORE REFERRAL TOKEN (r) IN LOCALSTORAGE */
  const r = searchParams.get('r');
  if (r) {
    localStorage.setItem('r', r);
  }

  if (searchParams.get('utm_medium') !== null) {
    // @ts-expect-error ts-migrate(2345) FIXME: Argument of type 'string | null' is not assignable... Remove this comment to see the full error message
    localStorage.setItem('utm_medium', searchParams.get('utm_medium'));
    // @ts-expect-error ts-migrate(2345) FIXME: Argument of type 'string | null' is not assignable... Remove this comment to see the full error message
    localStorage.setItem('utm_source', searchParams.get('utm_source'));
    // @ts-expect-error ts-migrate(2345) FIXME: Argument of type 'string | null' is not assignable... Remove this comment to see the full error message
    localStorage.setItem('utm_term', searchParams.get('utm_term'));
    // @ts-expect-error ts-migrate(2345) FIXME: Argument of type 'string | null' is not assignable... Remove this comment to see the full error message
    localStorage.setItem('utm_campaign', searchParams.get('utm_campaign'));
    // @ts-expect-error ts-migrate(2345) FIXME: Argument of type 'string | null' is not assignable... Remove this comment to see the full error message
    localStorage.setItem('utm_content', searchParams.get('utm_content'));
  }
  /* END STORE REFERRAL TOKEN IN LOCALSTORAGE */

  /* Listen for last message from firestore */
  useEffect(() => {
    if (
      !user ||
      !user.uid ||
      process.env.REACT_APP_FORCE_WEB_VIEW === '1' ||
      (window as $TSFixMe).ReactNativeWebView
    ) {
      return () => {};
    }
    const appNotificationsRef = doc(
      collection(firestoreDb, 'app-notifications'),
      user.uid,
    );
    setDoc(appNotificationsRef, {}, {merge: true});
    try {
      const unsub = onSnapshot(
        appNotificationsRef,
        (snapshot) => {
          const data = snapshot.data();
          if (
            data &&
            data?.timestamp &&
            Number(data.timestamp) > systemStartTime
          ) {
            process.env.NODE_ENV === 'development' &&
              console.log('Received lastMsg data!!!!!');
            process.env.NODE_ENV === 'development' && console.log(data);
            setLastMsg(data);
            if (
              ['message', 'central'].includes(data.model) &&
              data.type !== 'typing' &&
              !window.location.pathname.includes('/messages')
            ) {
              setUnreadMessageCount((prev: number) => prev + 1);
            }
          }
        },
        (err) => {
          // On logout the listener briefly outlives the session and the
          // rules answer permission-denied; without this handler that
          // surfaces as an uncaught error on /login.
          if (err?.code !== 'permission-denied') {
            process.env.NODE_ENV === 'development' && console.error(err);
          }
        },
      );
      return () => {
        unsub ? unsub() : () => {};
      };
    } catch (e) {
      process.env.NODE_ENV === 'development' && console.log('Caught it!');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  useEffect(() => {
    if (user) {
      setFirebaseUserProperties({service_plan: user?.servicePlan || 'FREE'});
    }
  }, [user]);

  // ServeWeb.ts injects a static homepage hero (id="prehydration-hero") as a
  // SIBLING of #root so logged-out visitors see real content before any
  // JavaScript runs. React never touches it (it's outside #root), so it must
  // be removed by hand. MarketingPage removes it when the real marketing
  // iframe finishes loading; this covers signed-in users, whose homepage
  // renders a dashboard instead of MarketingPage, so it never lingers over
  // their app.
  useEffect(() => {
    if (user?.uid) {
      document.getElementById('prehydration-hero')?.remove();
    }
  }, [user]);

  /* ENABLE LOG ROCKET AND SENTRY MONITORING */
  useEffect(() => {
    if (process.env.NODE_ENV !== 'development') {
      Sentry.init({
        dsn: process.env.REACT_APP_SENTRY_DSN,
        // Stale-deploy chunk 404s are handled by a transparent one-time
        // reload (see common/chunkReload); drop them so they don't show up
        // as errors. Secured requests skipped while signed out throw a named
        // SignedOutError (see @bidmii/common FetchRequest) — expected
        // behaviour, dropped too. Every other event passes through untouched.
        beforeSend: (event, hint) => {
          if (isChunkLoadError(hint?.originalException)) {
            return null;
          }
          if (
            event.exception?.values?.some((ex) => ex.type === 'SignedOutError')
          ) {
            return null;
          }
          return event;
        },
        // Everyday connectivity blips, scanner artifacts, and browser
        // housekeeping — not application errors.
        ignoreErrors: [
          'Network request failed',
          'Failed to fetch',
          'Load failed',
          'NetworkError when attempting to fetch resource',
          'Object Not Found Matching Id',
          'ResizeObserver loop',
          'AbortError',
          'database connection is closing',
          'Database deleted by request of the user',
          'object store by key range',
        ],
      });
    }
  }, []);

  useEffect(() => {
    if (enableLogrocket) {
      process.env.NODE_ENV === 'development' &&
        console.log('ENABLING LOGROCKET MONITORING');
      LogRocket.init('pimcf6/bidmii-beta');
    }
  }, [enableLogrocket]);

  // Block context menu in production
  if (enableLogrocket) {
    window.document.oncontextmenu = () => false;
  }

  return (
    <BetaGate>
      <div className="App">
        <ColdStartOverlay visible={coldStartVisible} ready={serverReady} />
        <StyledEngineProvider injectFirst>
          <ThemeProvider theme={theme}>
            <LocalizationProvider dateAdapter={AdapterMoment}>
              <HelmetProvider>
                <BrowserRouter>
                  <Switch>
                    <Route path="/" component={MainRouter} />
                    <Route render={() => <h1>Page not found</h1>} />
                  </Switch>
                </BrowserRouter>
              </HelmetProvider>
            </LocalizationProvider>
          </ThemeProvider>
        </StyledEngineProvider>
      </div>
    </BetaGate>
  );
};

export default App;
